// Is there an offline copy of the app on this device, and is it in charge?
//
// "It does not work offline" is not a bug report anybody can act on, and the
// launch path has exactly one place it can fail invisibly: the service worker
// either installed and took control, or it did not, and nothing on screen ever
// says which. On a phone there is no console to check. These are the answers.
//
// The decisive row is `controlling`. A registration that exists but controls
// nothing serves no navigation, so the next offline launch falls through to the
// network and the browser shows its own error page.

export interface OfflineCopy {
  /** A worker is registered for this origin. */
  registered: boolean;
  /** …and it is serving this page. Without this, offline does not work. */
  controlling: boolean;
  /** `active`, `waiting`, `installing`, or undefined when nothing is registered. */
  state: string | undefined;
  /** How many files the worker has saved. Zero means an install that failed. */
  files: number;
  /** Whether the start page — the one an offline launch needs — is among them. */
  startPage: boolean;
}

/// Workbox names its precache `workbox-precache-v2-<scope>`. Found by prefix
/// rather than rebuilt from the parts: the suffix is the registration scope, and
/// a mismatch would read as "nothing cached" — the exact failure being hunted.
async function precacheName(): Promise<string | undefined> {
  const names = await caches.keys();
  return names.find((name) => name.startsWith('workbox-precache'));
}

export async function offlineCopy(): Promise<OfflineCopy> {
  const absent: OfflineCopy = {
    registered: false,
    controlling: false,
    state: undefined,
    files: 0,
    startPage: false,
  };
  if (!('serviceWorker' in navigator)) {
    return absent;
  }

  const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  const worker = registration?.active ?? registration?.waiting ?? registration?.installing;

  const name = await precacheName().catch(() => undefined);
  const requests = name === undefined ? [] : await (await caches.open(name)).keys();

  return {
    registered: registration !== undefined,
    controlling: navigator.serviceWorker.controller !== null,
    state: worker?.state,
    files: requests.length,
    // The navigation fallback is precached under '/' (see vite.config.ts); the
    // cache key carries a `__WB_REVISION__` query string, so compare paths.
    startPage: requests.some((request) => new URL(request.url).pathname === '/'),
  };
}

/// Throw the offline copy away and fetch a fresh one. For the case where the
/// worker installed badly and is now stuck that way — a reinstall of the app is
/// the only other cure, and that costs the user every verified company.
///
/// Touches Cache Storage ONLY. Messages, contacts and device keys live in
/// IndexedDB and are never in scope here: losing them means SMS re-verification
/// per company, which is not a thing a diagnostics button may do.
export async function repairOfflineCopy(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  await registration?.unregister();
  for (const name of await caches.keys()) {
    if (name.startsWith('workbox-')) {
      await caches.delete(name);
    }
  }
  // The fresh worker only takes control of a page it did not start under, so the
  // caller reloads; registering here means the precache is already downloading
  // by the time it does.
  await navigator.serviceWorker.register('/sw.js');
}
